/**
 * ProspectProfileSection Component
 * Displays tags and metrics for the prospect profile.
 *
 * Notes (recruiting notes) live in the dedicated Notes tab — this section no
 * longer surfaces an inline notes textarea.
 */

import type { ProspectResponse } from "../../../../types/api";
import type { DefaultTagDefinition } from "../../../../types/tags";
import { TagsList } from "../../../../components/tags/TagsList";
import { PositionPlayerMetricsSection } from "./PositionPlayerMetricsSection";
import { PitcherMetricsSection } from "./PitcherMetricsSection";
import type { PitchConfig } from "../../../../constants/pitchConfig";
import type { RosterSport } from "../../../../constants/sport";
import { ProspectLinksSection } from "./links/ProspectLinksSection";

interface ProspectProfileSectionProps {
  prospect: ProspectResponse;
  activeMetricsTab: "positionPlayer" | "pitcher";
  onMetricsTabChange: (tab: "positionPlayer" | "pitcher") => void;
  tagDefinitions: DefaultTagDefinition[];
  tagsByName: Record<string, string[]>;
  onTagPress: (tagName: string) => void;
  tagError: string | null;
  metricsError: string | null;
  onPositionPlayerMetricUpdate: (
    field: string,
    value: number | null
  ) => void;
  onPitcherMetricUpdate: (field: string, value: number | null) => void;
  pitchConfigs: PitchConfig[];
  currentUserId?: string;
  sport?: string | null;
  /** Normalised sport; the links section is baseball only. */
  rosterSport: RosterSport;
}

export function ProspectProfileSection({
  prospect,
  activeMetricsTab,
  onMetricsTabChange,
  tagDefinitions,
  tagsByName,
  onTagPress,
  tagError,
  metricsError,
  onPositionPlayerMetricUpdate,
  onPitcherMetricUpdate,
  pitchConfigs,
  currentUserId,
  sport,
  rosterSport,
}: ProspectProfileSectionProps) {
  return (
    <>
      {/* Tags Section */}
      {tagDefinitions.length > 0 && (
        <div className="prospect-viewer-panel__section">
          <TagsList
            definitions={tagDefinitions}
            tagsByName={tagsByName}
            onTagPress={onTagPress}
          />
          {tagError && (
            <div className="prospect-viewer-panel__error">{tagError}</div>
          )}
        </div>
      )}

      {/* Links (renders nothing for softball) */}
      <ProspectLinksSection prospectId={prospect.id} sport={rosterSport} />

      {/* Sub-tabs */}
      <div className="prospect-viewer-panel__sub-tabs">
        <button
          className={`prospect-viewer-panel__sub-tab ${
            activeMetricsTab === "positionPlayer"
              ? "prospect-viewer-panel__sub-tab--active"
              : ""
          }`}
          onClick={() => onMetricsTabChange("positionPlayer")}
        >
          Position Player
        </button>
        <button
          className={`prospect-viewer-panel__sub-tab ${
            activeMetricsTab === "pitcher"
              ? "prospect-viewer-panel__sub-tab--active"
              : ""
          }`}
          onClick={() => onMetricsTabChange("pitcher")}
        >
          Pitcher
        </button>
      </div>

      {/* Metrics Section */}
      <div className="prospect-viewer-panel__metrics-section">
        {metricsError && (
          <div className="prospect-viewer-panel__error">{metricsError}</div>
        )}

        {activeMetricsTab === "positionPlayer" ? (
          <PositionPlayerMetricsSection
            metrics={prospect?.positionPlayerMetrics || {}}
            onMetricChange={onPositionPlayerMetricUpdate}
            prospectId={prospect.id}
            metricEntries={prospect.metricEntries}
            currentUserId={currentUserId}
            sport={sport}
          />
        ) : (
          <PitcherMetricsSection
            metrics={prospect?.pitcherMetrics || {}}
            onMetricChange={onPitcherMetricUpdate}
            pitchConfigs={pitchConfigs}
            prospectId={prospect.id}
            metricEntries={prospect.metricEntries}
            currentUserId={currentUserId}
          />
        )}
      </div>
    </>
  );
}
